import { useRef, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Upload, X, GripVertical } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const MultiImageUploader = ({ images = [], onChange, testId = 'multi-img' }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const uploadFiles = async (files) => {
    const valid = Array.from(files).filter(f => {
      const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
      if (!allowed.includes(f.type)) { toast.error(`${f.name}: unsupported type`); return false; }
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: too large (max 10MB)`); return false; }
      return true;
    });
    if (valid.length === 0) return;

    setUploading(true);
    const fd = new FormData();
    valid.forEach(f => fd.append('files', f));

    try {
      const res = await axios.post(`${API}/upload/bulk`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      // Save relative paths — portable across any domain
      const newUrls = res.data.filter(r => r.url).map(r => r.url);
      if (newUrls.length > 0) {
        onChange([...images, ...newUrls]);
        toast.success(`${newUrls.length} image(s) uploaded!`);
      }
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files);
  }, [images]);

  const handleDragOver = useCallback((e) => { e.preventDefault(); setDragOver(true); }, []);
  const handleDragLeave = useCallback(() => setDragOver(false), []);

  const removeImage = (idx) => {
    onChange(images.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-3" data-testid={testId}>
      {/* Image previews */}
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {images.map((img, idx) => (
            <div key={idx} className="relative group">
              <img src={img} alt={`Product ${idx + 1}`} className="h-20 w-20 object-cover rounded border border-gray-200" />
              <button type="button" onClick={() => removeImage(idx)}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`${testId}-remove-${idx}`}>
                <X size={12} />
              </button>
              {idx === 0 && <span className="absolute bottom-0 left-0 bg-[#D4AF37] text-white text-[8px] px-1">Main</span>}
            </div>
          ))}
        </div>
      )}

      {/* Drop zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
          dragOver ? 'border-[#D4AF37] bg-[#D4AF37]/5' : 'border-gray-300 hover:border-gray-400'
        }`}
        onClick={() => fileRef.current?.click()}
        data-testid={`${testId}-dropzone`}
      >
        <input ref={fileRef} type="file" accept="image/*" multiple onChange={(e) => uploadFiles(e.target.files)} className="hidden" data-testid={`${testId}-file`} />
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-[#D4AF37]">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-current border-t-transparent" />
            <span className="text-sm">Uploading...</span>
          </div>
        ) : (
          <div className="text-gray-500">
            <Upload size={24} className="mx-auto mb-2 text-gray-400" />
            <p className="text-sm font-medium">Drag & drop images here</p>
            <p className="text-xs text-gray-400 mt-1">or click to browse (JPG, PNG, WebP, max 10MB each)</p>
          </div>
        )}
      </div>
    </div>
  );
};
